import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import Info from "../../common/info/info";
import classes from "./main.module.css";
import {getServers} from "../../../utils/api";

import nid from "../app/servers/nid.png";
import itali from "../app/servers/itali.png";
import polsh from "../app/servers/polsh.png";
import kaz from "../app/servers/kaz.png";
import latvia from "../app/servers/latvia.png";
import tur from "../app/servers/tur.png";
import rus from "../app/servers/rus.png";
import usa from "../app/servers/usa.png";
import japan from "../app/servers/japan.png";
import spain from "../app/servers/spain.png";
import france from "../app/servers/france.png";
import germany from "../app/servers/germany.png";
import wireguard from "../app/servers/wireguard.png";
import amneziaWG from "../app/servers/amneziaWG.png";
import vless from "../app/servers/vless.png";

import logo from '../../../images/logo.jpg'

function getCountryIcon(country) {
    switch (country) {
        case 'Нидерланды':
            return nid;
        case 'Италия':
            return itali;
        case 'Польша':
            return polsh;
        case 'Казахстан':
            return kaz;
        case 'Латвия':
            return latvia;
        case 'Турция':
            return tur;
        case 'Россия':
            return rus;
        case 'США':
            return usa;
        case 'Япония':
            return japan;
        case 'Испания':
            return spain;
        case 'Франция':
            return france;
        case 'Германия':
            return germany;
        default:
            return null;
    }
}

function getVlessProtocol(client) {
    const c = String(client || '').trim();
    if (!c.toLowerCase().startsWith('vless')) return null;
    const parts = c.split(' ').filter(Boolean);
    return parts.length >= 2 ? parts[1] : null;
}

/** Одна карточка на страну: приоритет онлайн, затем меньший id. */
function dedupeServersByCountry(servers) {
    const byCountry = new Map();
    const sorted = (servers || []).slice().sort((a, b) => {
        const ao = a?.online ? 1 : 0;
        const bo = b?.online ? 1 : 0;
        if (ao !== bo) return bo - ao;
        return (a?.id ?? 0) - (b?.id ?? 0);
    });
    for (const s of sorted) {
        const country = String(s?.country ?? '').trim();
        if (!country) continue;
        if (!byCountry.has(country)) byCountry.set(country, s);
    }
    return Array.from(byCountry.values()).sort((a, b) =>
        (a?.country || '').localeCompare(b?.country || '', 'ru'),
    );
}

/** VLESS: одна секция, но до трёх (и более) карточек на страну — по разным протоколам. */
function dedupeVlessByCountryAndProtocol(servers) {
    const byKey = new Map();
    const sorted = (servers || []).slice().sort((a, b) => {
        const ao = a?.online ? 1 : 0;
        const bo = b?.online ? 1 : 0;
        if (ao !== bo) return bo - ao;
        return (a?.id ?? 0) - (b?.id ?? 0);
    });
    for (const s of sorted) {
        const country = String(s?.country ?? '').trim();
        if (!country) continue;
        const proto = getVlessProtocol(s.client) || 'default';
        const key = `${country}\u0000${proto}`;
        if (!byKey.has(key)) byKey.set(key, s);
    }
    return Array.from(byKey.values()).sort((a, b) => {
        const ca = (a?.country || '').localeCompare(b?.country || '', 'ru');
        if (ca !== 0) return ca;
        const pa = getVlessProtocol(a.client) || '';
        const pb = getVlessProtocol(b.client) || '';
        return pa.localeCompare(pb, 'ru');
    });
}

const MOBILE_BREAKPOINT = '(max-width: 860px)';

function useIsMobileLayout() {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia(MOBILE_BREAKPOINT).matches,
    );

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_BREAKPOINT);
        const onChange = () => setIsMobile(mq.matches);
        onChange();
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    return isMobile;
}

/** На мобильных — раскрывающийся блок; на десктопе — обычная секция. */
function ProtocolServerSection({isMobile, iconSrc, title, count, children}) {
    const headerInner = (
        <>
            <span className={classes.clientSectionTitleRow}>
                <img className={classes.clientSectionHeaderIcon} src={iconSrc} alt="" />
                <span>{title}</span>
            </span>
            {isMobile ? (
                <span className={classes.clientSectionMeta}>
                    <span className={classes.clientSectionCount}>{count}</span>
                    <span className={classes.clientSectionChevron} aria-hidden />
                </span>
            ) : null}
        </>
    );

    if (isMobile) {
        return (
            <details className={classes.clientSectionDetails}>
                <summary className={classes.clientSectionSummary}>{headerInner}</summary>
                {children}
            </details>
        );
    }

    return (
        <div className={classes.clientSection}>
            <div className={classes.clientSectionHeader}>
                <img className={classes.clientSectionHeaderIcon} src={iconSrc} alt="" />
                <span>{title}</span>
            </div>
            {children}
        </div>
    );
}

const ServersPreview = () => {
    const [servers, setServers] = useState([]);
    const isMobile = useIsMobileLayout();

    useEffect(() => {
        const run = async () => {
            try {
                const res = await getServers();
                const list = Array.isArray(res?.res) ? res.res : [];
                if (list.length > 0) {
                    // Дедуп на случай повторов в ответе API/БД.
                    const uniqueById = new Map();
                    for (const s of list) {
                        if (!s || typeof s.id !== 'number') continue;
                        if (!uniqueById.has(s.id)) uniqueById.set(s.id, s);
                    }
                    setServers(Array.from(uniqueById.values()));
                } else {
                    // Оставляем servers пустым, но не валим рендер.
                    setServers([]);
                }
            } catch (e) {
                // Best-effort: marketing page should still render.
            }
        };
        run();
    }, []);

    const clientGroups = useMemo(() => {
        const sortFn = (a, b) => {
            const ao = a?.online ? 1 : 0;
            const bo = b?.online ? 1 : 0;
            if (ao !== bo) return bo - ao;
            return (a?.country || '').localeCompare(b?.country || '');
        };

        const wireguard = [];
        const amnezia = [];
        const vless = [];

        for (const s of (servers || [])) {
            const c = s?.client ? String(s.client).trim() : '';
            if (!c) continue;

            const lc = c.toLowerCase();
            if (lc === 'wireguard' || lc.startsWith('wireguard')) wireguard.push(s);
            else if (lc === 'amneziawg' || lc.startsWith('amnezia') || lc.includes('amnezia')) amnezia.push(s);
            else if (lc.startsWith('vless')) vless.push(s);
        }

        return {
            wireguard: dedupeServersByCountry(wireguard.slice().sort(sortFn)),
            amnezia: dedupeServersByCountry(amnezia.slice().sort(sortFn)),
            vless: dedupeVlessByCountryAndProtocol(vless.slice().sort(sortFn)),
        };
    }, [servers]);

    return (
        <div className={classes.serversWrap}>
            <div className={classes.serversHeader}>Сервера</div>

            {clientGroups.wireguard.length > 0 && (
                <ProtocolServerSection
                    isMobile={isMobile}
                    iconSrc={wireguard}
                    title="Wireguard"
                    count={clientGroups.wireguard.length}
                >
                    <div className={classes.serversGrid}>
                        {clientGroups.wireguard.map((s) => (
                            <div key={s.country} className={classes.serverCard}>
                                <div className={classes.serverCardRow}>
                                    <div className={classes.serverCardLeft}>
                                        <img
                                            className={classes.serverCountryIcon}
                                            src={getCountryIcon(s.country)}
                                            alt=""
                                            style={{display: getCountryIcon(s.country) ? 'block' : 'none'}}
                                        />
                                        <div className={classes.serverCountry}>{s.country}</div>
                                    </div>
                                    <div className={classes.serverCardStatus}>
                                        <span className={classes.onlineDot} style={{background: s.online ? '#34d399' : '#94a3b8'}} />
                                        {s.online ? 'онлайн' : 'офлайн'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </ProtocolServerSection>
            )}

            {clientGroups.amnezia.length > 0 && (
                <ProtocolServerSection
                    isMobile={isMobile}
                    iconSrc={amneziaWG}
                    title="AmneziaWG"
                    count={clientGroups.amnezia.length}
                >
                    <div className={classes.serversGrid}>
                        {clientGroups.amnezia.map((s) => (
                            <div key={s.country} className={classes.serverCard}>
                                <div className={classes.serverCardRow}>
                                    <div className={classes.serverCardLeft}>
                                        <img
                                            className={classes.serverCountryIcon}
                                            src={getCountryIcon(s.country)}
                                            alt=""
                                            style={{display: getCountryIcon(s.country) ? 'block' : 'none'}}
                                        />
                                        <div className={classes.serverCountry}>{s.country}</div>
                                    </div>
                                    <div className={classes.serverCardStatus}>
                                        <span className={classes.onlineDot} style={{background: s.online ? '#34d399' : '#94a3b8'}} />
                                        {s.online ? 'онлайн' : 'офлайн'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </ProtocolServerSection>
            )}

            {clientGroups.vless.length > 0 && (
                <ProtocolServerSection
                    isMobile={isMobile}
                    iconSrc={vless}
                    title="VLESS"
                    count={clientGroups.vless.length}
                >
                    <div className={classes.serversGrid}>
                        {clientGroups.vless.map((s) => {
                            const proto = getVlessProtocol(s.client);
                            return (
                                <div
                                    key={`${s.country}-${proto || 'default'}`}
                                    className={classes.serverCard}
                                >
                                    <div className={classes.serverCardRow}>
                                        <div className={classes.serverCardLeft}>
                                            <img
                                                className={classes.serverCountryIcon}
                                                src={getCountryIcon(s.country)}
                                                alt=""
                                                style={{display: getCountryIcon(s.country) ? 'block' : 'none'}}
                                            />
                                            <div className={classes.serverCountryColumn}>
                                                <div className={classes.serverCountry}>{s.country}</div>
                                                {proto ? (
                                                    <div className={classes.serverProtocol}>{proto}</div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className={classes.serverCardStatus}>
                                            <span className={classes.onlineDot} style={{background: s.online ? '#34d399' : '#94a3b8'}} />
                                            {s.online ? 'онлайн' : 'офлайн'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ProtocolServerSection>
            )}
        </div>
    );
};

const Main = () => {
    return (
        <div className={classes.page}>
            <div className={classes.hero}>
                <div className={classes.heroMax}>
                    <div className={classes.navbar}>
                        <div className={classes.logo}>
                            <img
                                className={classes.logoImg}
                                src={logo}
                                alt="EasyFast VPN"
                            />
                            <span className={classes.logoAccent}>EasyFast</span>
                            VPN
                        </div>
                        <div className={classes.navActions}>
                            <Link className={classes.paymentBtn} to="/payment">
                                Пополнить
                            </Link>
                            <Link className={classes.loginBtn} to="/auth">
                                Войти
                            </Link>
                        </div>
                    </div>
                </div>

                <div className={classes.heroContent}>
                    <div className={classes.title}>Простой и быстрый VPN</div>
                    <div className={classes.subtitle}>
                        WireGuard, AmneziaWG и VLESS. Работает на любых устройствах — Android, iPhone, MacBook и Windows.
                    </div>

                    <div className={classes.ctaStack}>
                        <div className={classes.ctaRow}>
                            <a
                                className={classes.primaryLink}
                                href="https://t.me/EasyFastVPNBot?start=NDgwNjQyMTE0"
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Открыть Telegram-бота EasyFast VPN — 2 недели в подарок"
                            >
                                <span className={classes.primaryBtn}>
                                    <span className={classes.primaryBtnMain}>Начать в Telegram</span>
                                    <span className={classes.primaryBtnSub}>2 недели в подарок за вход через бота</span>
                                </span>
                            </a>
                        </div>
                        <p className={classes.ctaHint}>
                            Если Telegram недоступен — используйте сайт: оформите подписку, подключите VPN и привяжите Telegram позже. Бонус начислится автоматически.
                        </p>
                    </div>

                    <div className={classes.bullets}>
                        <div className={classes.bullet}>🔒 Защита и приватность</div>
                        <div className={classes.bullet}>🚀 Высокая скорость</div>
                        <div className={classes.bullet}>🌍 Выбор страны</div>
                    </div>
                </div>
            </div>

            <div className={classes.serversSection}>
                <ServersPreview />
            </div>
            <Info />
        </div>
    );
};

export default Main;